/**
 * The org reference-data store (plan §3.3, wave line :197).
 *
 * Replaces the eight org-scoped Firestore listeners the app shell blocks on with
 * one `org_bootstrap` call plus invalidation-driven refetches. It owns the
 * slices; it does not own their presentation — mapping these into the 40-odd
 * keys `UserContext` exposes is the adapter's job, and is driven by a recorded
 * fixture so the shape is proven rather than asserted (gate item H9).
 *
 * WHAT THIS DELIBERATELY DOES NOT DO
 *
 * It carries the ORG-scoped slices only. Three of the eighteen listeners are
 * user-scoped — the user record, admin control sessions, billing — and belong to
 * the `user:{uid}` channel (charter §16.4). Two more are simply deleted:
 * `monthlyAggregates` has no production consumer, and `billingData` has none
 * either (the plan's inventory missed the second one).
 *
 * Contacts and donors leave the bootstrap on purpose (plan :146): they are
 * unbounded per org and become query-backed pickers. Loading every contact into
 * session memory was a Firestore-shaped decision, not a requirement.
 *
 * ON REFETCH GRANULARITY. A signal names the topics that changed, so only those
 * slices are refetched. The full-invalidate path reloads everything, because by
 * definition we no longer know what we missed.
 */

/**
 * Which bootstrap slices a given event topic invalidates.
 *
 * An UNKNOWN topic refetches everything (see invalidate()) — the safe reading
 * of "something changed and I don't know what". That makes every ledger and
 * reconciliation topic that is NOT listed here a full `org_bootstrap` round
 * trip per event: one post, one delete, one reconciliation tick each cost the
 * chart of accounts again. They are listed as `[]` because none of them
 * touches a bootstrap slice — the ledger is the list store's problem
 * (src/data/ledgerList.js, fed through src/data/orgEvents.js), and
 * `org_bootstrap` (0008) carries no reconciliation status: that lives in
 * `v_reconciliation_overview`, which the reconciliation screens read directly.
 */
const TOPIC_SLICES = {
  "accounts.changed": ["accounts", "funds", "account_groups"],
  "funds.changed": ["funds", "account_groups"],
  "labels.changed": ["labels", "label_groups"],
  "org.changed": ["org", "features", "billing"],
  "subscription.changed": ["features", "billing"],
  // The ledger is not reference data.
  "entry.posted": [],
  "entry.changed": [],
  "entry.deleted": [],
  // Nor is reconciliation state; nothing in org_bootstrap changes on any of these.
  "reconciliation.started": [],
  "reconciliation.changed": [],
  "reconciliation.completed": [],
  "reconciliation.reopened": [],
  "reconciliation.discarded": [],
  // A payee minted by create_contact (0260): contacts left the bootstrap in N2
  // (query-backed pickers), so nothing here changes. Without this row it would
  // fall through to "unknown topic → refetch everything" on every new payee.
  "contact.created": [],
  // Same for a donor minted by create_donor (0261); donors left the bootstrap
  // with contacts.
  "donor.created": [],
};

const ALL_SLICES = ["org", "accounts", "funds", "labels", "account_groups", "label_groups", "features", "billing"];

/**
 * @param {object} opts
 * @param {(orgId: string) => Promise<object>} opts.fetchBootstrap  — the org_bootstrap RPC.
 * @param {(slices: string[]) => void} [opts.onChange]              — notified after each refresh.
 */
export function createRefdata({ fetchBootstrap, onChange }) {
  let orgId = null;
  let data = null;
  let loading = false;
  let error = null;
  let loadedAt = null;
  let inFlight = null;
  // The ONE re-load every caller that arrived mid-load is waiting on.
  let queued = null;
  // Bumped on every load START. Only the newest load may publish its payload —
  // see load().
  let generation = 0;

  const snapshot = () => ({
    orgId,
    loading,
    error,
    loadedAt,
    // `loaded` is the flag the shell blocks on, and it must mean "safe to
    // render", not "a request finished". A failed load leaves it false.
    loaded: Boolean(data) && !error,
    ...(data ?? {}),
  });

  async function load() {
    if (!orgId) return snapshot();
    // Captured, not re-read: everything below has to know WHICH org and WHICH
    // load this is, because both can change while the RPC is in flight.
    const forOrg = orgId;
    const gen = (generation += 1);
    // A superseded load must not touch shared state at all. `snapshot()` already
    // reports the CURRENT orgId, so publishing a payload fetched for a previous
    // org would put that tenant's accounts, funds, members and billing under
    // this org's identity — the worst outcome this layer can produce.
    const superseded = () => gen !== generation || forOrg !== orgId;

    loading = true;
    error = null;
    try {
      const next = await fetchBootstrap(forOrg);
      if (superseded()) return snapshot();
      data = next;
      loadedAt = Date.now();
    } catch (e) {
      if (superseded()) return snapshot();
      error = e;
      // Keep whatever we had: a transient refetch failure must not blank a
      // screen that was working. `loaded` already reflects the error.
    } finally {
      // Only the newest load owns the flag; an abandoned one clearing it would
      // report "not loading" while a load is still running.
      if (gen === generation) loading = false;
    }
    onChange?.(ALL_SLICES);
    return snapshot();
  }

  const runLoad = () => {
    const run = load().finally(() => { if (inFlight === run) inFlight = null; });
    inFlight = run;
    return run;
  };

  /**
   * Serialise WITHOUT joining — the same defect, and the same fix, as
   * resync.js's fetchSinceSerialised.
   *
   * Handing an invalidation the load already in flight is silent staleness:
   * that load's `org_bootstrap` query may have executed SERVER-SIDE before the
   * change that prompted the invalidation committed. The caller resolves with
   * the pre-change snapshot, the invalidation is consumed, `loaded` stays true,
   * and nothing refetches until the NEXT accounts/funds/labels/org event —
   * which, for the org that just created its last account, is never. Two
   * `accounts.changed` events 50ms apart is all it takes.
   *
   * So mid-load callers wait on ONE re-load started after the current one ends.
   */
  const loadSerialised = () => {
    if (!inFlight) return runLoad();
    queued ??= inFlight
      // A failed load must still be followed by the re-load: the second event
      // is no less real for the first request having thrown.
      .catch(() => {})
      .then(() => { queued = null; return runLoad(); });
    return queued;
  };

  return {
    get state() { return snapshot(); },

    /** Point at an org and fetch. Switching orgs drops the previous data first. */
    async open(nextOrgId) {
      if (orgId !== nextOrgId) {
        data = null;
        loadedAt = null;
        error = null;
        // Abandon anything in flight for the PREVIOUS org rather than waiting
        // on it or, worse, letting a mid-load caller be served by it. The
        // abandoned run is already inert (load()'s generation check refuses to
        // publish it), and blocking a deliberate org switch behind the old
        // org's RPC is a stall the user did not ask for.
        inFlight = null;
        queued = null;
      }
      orgId = nextOrgId;
      return loadSerialised();
    },

    close() {
      orgId = null; data = null; loadedAt = null; error = null;
      // Same reason as open(): whatever is in flight belongs to a closed store.
      inFlight = null; queued = null;
      generation += 1;
    },

    /**
     * Apply an invalidation from resync. `events` is what actually changed, so
     * the topics decide whether a refetch is needed at all — a ledger-only batch
     * touches no reference data and must not cost a round trip.
     */
    async invalidate(events) {
      if (!orgId) return snapshot();
      const slices = new Set();
      for (const e of events ?? []) {
        for (const s of TOPIC_SLICES[e.topic] ?? ALL_SLICES) slices.add(s);
      }
      if (slices.size === 0) return snapshot();
      // org_bootstrap is one round trip for everything, so a partial refresh is
      // not cheaper than a full one — the slice set is what we TELL callers
      // changed, so they can re-render narrowly.
      const next = await loadSerialised();
      onChange?.([...slices]);
      return next;
    },

    /** The unrecoverable-gap path: we no longer know what we missed. */
    async invalidateAll() {
      if (!orgId) return snapshot();
      return loadSerialised();
    },
  };
}

export const __testing = { TOPIC_SLICES, ALL_SLICES };
